import React, { useMemo } from 'react'
import { Button } from '../../../components/Button/Button'
import { ReactComponent as ZocksLogo } from '../../../assets/ZocksLogo.svg'
import { ReactComponent as BulkStepIcon } from '../../../assets/images/cross-client-match.svg'
import { ReactComponent as LocalStepIcon } from '../../../assets/images/cross-client-local-res-step.svg'
import { ReactComponent as AiStepIcon } from '../../../assets/images/cross-client-ai-step.svg'
import { type CrossClientQueryAtomicClause } from '../../../models/CrossClient'
import { type CrossClientReviewState } from './hooks'
import { getIntegrationSourceDisplayNames, joinNames } from '../utils'

interface CrossClientChatReviewProps {
  reviewState: CrossClientReviewState
}

type SummaryStepKey = 'bulk' | 'local' | 'ai'

interface SummaryStep {
  key: SummaryStepKey
  bold: boolean
  title: React.ReactNode
  description?: React.ReactNode
}

type TimelineItem =
  | { kind: 'lead'; key: string; content: React.ReactNode }
  | { kind: 'criteria'; key: string; clauses: CrossClientQueryAtomicClause[] }
  | { kind: 'working'; key: string; text: string }
  | { kind: 'steps'; key: string; steps: SummaryStep[] }

const STEP_ICONS: Record<SummaryStepKey, React.FC<React.SVGProps<SVGSVGElement>>> = {
  bulk: BulkStepIcon,
  local: LocalStepIcon,
  ai: AiStepIcon,
}

export const CrossClientChatReview: React.FC<CrossClientChatReviewProps> = ({ reviewState }) => {
  const items = useMemo(() => buildTimeline(reviewState), [reviewState])

  return (
    <div className='d-flex flex-column gap-3 cross-client-breakdown'>
      {items.map((item) => (
        <TimelineRow key={item.key} item={item} />
      ))}
    </div>
  )
}

interface CrossClientChatReviewActionsProps {
  reviewState: CrossClientReviewState
  onViewResults: () => void
  onRestart: () => void
  isLoading: boolean
}

export const CrossClientChatReviewActions: React.FC<CrossClientChatReviewActionsProps> = ({ reviewState, onViewResults, onRestart, isLoading }) => {
  if (reviewState.kind === 'bulk-running') {
    return null
  }
  return (
    <div className='d-flex gap-2'>
      <Button variant='header' className='button-medium cross-client-chat-action' onClick={onRestart} disabled={isLoading}>
        Refine question
      </Button>
      <Button variant='primary' className='button-medium cross-client-chat-action' onClick={onViewResults} loading={isLoading}>
        View results
      </Button>
    </div>
  )
}

const ZocksBulletLogo: React.FC = () => (
  <div className='d-flex align-items-center justify-content-center bg-black ai-agent-chat-message-logo zocks-border-0.25 cross-client-breakdown__bullet--zocks'>
    <ZocksLogo width={12} height={12} />
  </div>
)

const TimelineRow: React.FC<{ item: TimelineItem }> = ({ item }) => {
  switch (item.kind) {
    case 'lead':
      return (
        <div className='position-relative d-flex align-items-start zocks-gap-0.75 cross-client-breakdown__step'>
          <ZocksBulletLogo />
          <div className='paragraph-medium mb-0'>{item.content}</div>
        </div>
      )
    case 'criteria':
      return (
        <ul className='position-relative d-flex flex-column gap-3 cross-client-breakdown__criteria'>
          {item.clauses.map((clause) => (
            <li key={clause.variable} className='d-flex align-items-center gap-2 cross-client-breakdown__criterion'>
              <i className='bi bi-x-diamond-fill cross-client-breakdown__criterion-icon' />
              <span>{clause.questionForm || clause.description}</span>
            </li>
          ))}
        </ul>
      )
    case 'working':
      return (
        <div className='position-relative d-flex align-items-center zocks-gap-0.75 cross-client-breakdown__step'>
          <ZocksBulletLogo />
          <p className='paragraph-medium mb-0 shimmering-loader-text'>{item.text}</p>
        </div>
      )
    case 'steps':
      return (
        <ol className='cross-client-breakdown__steps'>
          {item.steps.map((step) => {
            const Icon = STEP_ICONS[step.key]
            return (
              <li key={step.key} className='d-flex gap-2 align-items-start cross-client-breakdown__step-row'>
                <Icon width={16} height={16} className='cross-client-breakdown__step-icon' />
                <div className='d-flex flex-column gap-1'>
                  <div className={`paragraph-medium mb-0 ${step.bold ? 'medium' : ''}`}>{step.title}</div>
                  {step.description ? <div className='paragraph-small zocks-text-gray-600'>{step.description}</div> : null}
                </div>
              </li>
            )
          })}
        </ol>
      )
  }
}

const buildTimeline = (state: CrossClientReviewState): TimelineItem[] => {
  const clauses = state.atomicClauses
  const count = clauses.length
  const noun = count === 1 ? 'criterion' : 'criteria'

  const integrationNames = getIntegrationSourceDisplayNames(state.sources)
  const integrationsList = joinNames(integrationNames)

  const introContent = (
    <>
      <strong>{count}</strong> {noun} found.
    </>
  )

  switch (state.kind) {
    case 'bulk-running':
      return [
        {
          kind: 'lead',
          key: 'bulk-running:lead',
          content: (
            <>
              {introContent}{' '}
              {state.hasLocal && integrationsList
                ? `I'll start with a quick check, then use ${integrationsList} to confirm the rest.`
                : state.hasLocal
                  ? "I'll start with a quick check, then use external sources to confirm the rest."
                  : "I'll start with a quick check, then run a deeper analysis on the rest."}
            </>
          ),
        },
        { kind: 'criteria', key: 'bulk-running:criteria', clauses },
        { kind: 'working', key: 'bulk-running:working', text: 'Checking Client Profiles in Zocks' },
      ]
    case 'bulk-done': {
      const { resultSummary, hasLocal } = state
      const matchedCount = resultSummary.fullCount + resultSummary.partialCount
      const totalCount = resultSummary.totalCount
      const remaining = Math.max(totalCount - matchedCount, 0)
      const steps: SummaryStep[] = [
        {
          key: 'bulk',
          bold: true,
          title: (
            <>
              {matchedCount} of {totalCount.toLocaleString()} clients matched
            </>
          ),
          description: 'Criteria checked against Client Profiles in Zocks',
        },
      ]
      if (hasLocal && remaining > 0) {
        const remainingSuffix = integrationsList ? `using your connected ${integrationsList} accounts` : 'using your connected external sources'
        steps.push({
          key: 'local',
          bold: false,
          title: `I'll check the remaining ${remaining.toLocaleString()} ${remaining === 1 ? 'client' : 'clients'} ${remainingSuffix}`,
          description: "Results appear as they're found, usually within minutes",
        })
      }
      if (remaining > 0) {
        steps.push({
          key: 'ai',
          bold: false,
          title: hasLocal ? 'Want more coverage? You can run a deeper analysis after.' : 'Want more coverage? You can run a deeper analysis next.',
          description: "Gets more context and analysis per client when criteria couldn't be confirmed",
        })
      }
      return [
        { kind: 'lead', key: 'bulk-done:intro', content: introContent },
        { kind: 'criteria', key: 'bulk-done:criteria', clauses },
        { kind: 'lead', key: 'bulk-done:next', content: "Here's what happened and what's next:" },
        { kind: 'steps', key: 'bulk-done:steps', steps },
      ]
    }
    case 'no-bulk': {
      const steps: SummaryStep[] = []
      if (state.hasLocal) {
        const localTitle = integrationsList
          ? `I'll check your clients using your connected ${integrationsList} accounts`
          : "I'll check your clients using your connected external sources"
        steps.push({
          key: 'local',
          bold: false,
          title: localTitle,
          description: "Results appear as they're found, usually within minutes",
        })
      }
      steps.push({
        key: 'ai',
        bold: false,
        title: state.hasLocal ? 'Want more coverage? You can run a deeper analysis after.' : 'Want more coverage? You can run a deeper analysis next.',
        description: "Gets more context and analysis per client when criteria couldn't be confirmed",
      })
      return [
        { kind: 'lead', key: 'no-bulk:intro', content: introContent },
        { kind: 'criteria', key: 'no-bulk:criteria', clauses },
        { kind: 'lead', key: 'no-bulk:next', content: "Here's the plan:" },
        { kind: 'steps', key: 'no-bulk:steps', steps },
      ]
    }
  }
}
